import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useWaFilterStore } from 'src/api/wa/store/WaFilterStore';
import { useWaDisasterStore } from 'src/api/wa/store/WaRegionAndDisasterStore';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';

import DropdownSelect from 'src/ui/wards/common/select/DropdownSelect';
import Button from 'src/ui/wards/common/button/Button';
import { wardsNavigate } from 'src/ui/wards/router/WardsScreenRegistration';
import { WaScreensMap } from 'src/ui/wards/router/WardsScreenMap';
import { WA_Screen } from 'src/ui/wards/router/WardsScreens';
import { handleUpdateTimestamp } from 'src/ui/wards/common/util/dateUtils';

import {
  BusinessSituation,
  businessSummaryColumns,
  businessSummaryCustomStyles,
} from './BusinessSummaryColumns';
import {
  disasterColumns,
  disasterCustomStyles,
  transformToDisasterRows,
} from '../DisasterRegionColumns';
import { BusinessTableHeader } from './BusinessTableHeader';
import { FormRow } from 'src/ui/wards/common/form/FormRow';

export const BusinessSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { selectedDisaster } = useWaDisasterStore();
  const { salesOfficeOptions, workplaceFilterParameters, patchWorkplaceFilterParameters } =
    useWaFilterStore();
  const branchCode = workplaceFilterParameters.branchCode;

  const { fetchSummaryBySalesOffice, fetchSalesOfficeOptions, fetchDisasterAreaSalesOffices } =
    useWaWorkplaceService();

  const [tableData, setTableData] = useState<BusinessSituation[]>([]);
  const [highlightedOffices, setHighlightedOffices] = useState<string[]>([]);

  const [lastClickedTime, setLastClickedTime] = useState<string | null>(() =>
    localStorage.getItem('last_business_click_time')
  );

  const zigzagData = useMemo(
    () => transformToDisasterRows(salesOfficeOptions),
    [salesOfficeOptions]
  );

  const handleFetchData = useCallback(async () => {
    if (selectedDisaster?.id && branchCode) {
      try {
        const rawData = await fetchSummaryBySalesOffice(
          selectedDisaster.id,
          branchCode,
          workplaceFilterParameters.salesOfficeCode || ''
        );
        setTableData(rawData || []);
      } catch (error) {
        console.error('Error fetching summary:', error);
        setTableData([]);
      }
    }
  }, [
    fetchSummaryBySalesOffice,
    selectedDisaster?.id,
    branchCode,
    workplaceFilterParameters.salesOfficeCode,
  ]);

  useEffect(() => {
    handleFetchData();
  }, [selectedDisaster?.id, branchCode, handleFetchData]);

  useEffect(() => {
    if (branchCode) {
      fetchSalesOfficeOptions(branchCode);
    }
  }, [branchCode, fetchSalesOfficeOptions]);

  useEffect(() => {
    const fetchHighlighting = async () => {
      if (selectedDisaster?.id && branchCode) {
        try {
          const codes = await fetchDisasterAreaSalesOffices(selectedDisaster.id, branchCode);
          setHighlightedOffices(codes || []);
        } catch (e) {
          console.error('Highlighting fetch failed', e);
          setHighlightedOffices([]);
        }
      }
    };
    fetchHighlighting();
  }, [selectedDisaster?.id, branchCode, fetchDisasterAreaSalesOffices]);

  const salesOfficeDropdownOptions = salesOfficeOptions.map((office) => ({
    title: `${office.code} ${office.name}`,
    value: office.code,
  }));

  const selectedSalesOfficeOption =
    salesOfficeDropdownOptions.find(
      (option) => option.value === workplaceFilterParameters.salesOfficeCode
    ) || salesOfficeDropdownOptions[0];

  const handleSalesOfficeChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    patchWorkplaceFilterParameters({
      salesOfficeCode: event.target.value,
      sectionCode: '',
      squadCode: '',
    });
  };

  const handleButtonClick = () => {
    handleUpdateTimestamp('last_business_click_time', setLastClickedTime);
    handleFetchData();
  };

  useEffect(() => {
    handleUpdateTimestamp('last_business_click_time', setLastClickedTime);
  }, []);

  const backNavigation = () => {
    wardsNavigate(navigate, WaScreensMap[WA_Screen.BranchSummary].path);
  };

  return (
    <div className="mx-auto flex max-w-fit flex-col gap-2 pb-4">
      <div className="space-y-1 text-2xl font-bold">{t('wards.desktop.summary.title')}</div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col items-center gap-4 rounded-sm border-2 border-sky-600 p-3 shadow-sm sm:flex-row">
          <div className="w-full flex-1">
            <FormRow title={t('wards.common.salesOffice')} required>
              <DropdownSelect
                selectedValue={selectedSalesOfficeOption}
                optionValues={salesOfficeDropdownOptions}
                onChange={handleSalesOfficeChanged}
              />
            </FormRow>
          </div>
          <Button title={t('wards.common.update')} onClick={handleButtonClick} />
          {lastClickedTime && (
            <span className="self-center text-sm text-gray-600">{lastClickedTime}</span>
          )}
        </div>
        <div className="w-full rounded-sm border-2 border-sky-600 bg-gray-50 p-3 text-center text-sm">
          {t('wards.common.displayedContent')}
        </div>
      </div>

      <div className="flex h-fit flex-col overflow-hidden rounded-sm border border-gray-300 shadow-sm">
        <DataTable
          noDataComponent={<div className="p-4">{t('wards.common.noResults')}</div>}
          noTableHead
          columns={disasterColumns(highlightedOffices)}
          data={zigzagData}
          customStyles={disasterCustomStyles}
          dense
        />
      </div>

      <div className="flex w-full flex-col">
        <div className="mb-2 text-sm font-semibold tracking-wide">
          {t('wards.desktop.summary.salesOffice.title')}
        </div>

        <div className="w-full overflow-x-auto rounded-sm border border-gray-300 shadow-sm">
          <div className="inline-block min-w-max align-middle">
            <BusinessTableHeader t={t} />
            <DataTable
              noDataComponent={<div className="p-4">{t('wards.common.noResults')}</div>}
              noTableHead
              columns={businessSummaryColumns()}
              data={tableData}
              customStyles={businessSummaryCustomStyles}
              dense
              pagination
              paginationPerPage={10}
            />
          </div>
        </div>
      </div>

      <Button title={t('wards.common.back')} onClick={backNavigation} className="self-start" />
    </div>
  );
};
